import { Injectable } from '@angular/core';

export interface IfieldsTokens {
  cardSut: string;
  cvvSut: string;
}

export interface IfieldsVerification {
  x3dsActionCode: string;
  xCavv: string;
  xEci: string;
  xRefNum: string;
  x3dsAuthenticationStatus: string;
  x3dsSignatureVerificationStatus: string;
}

export type IfieldsFieldStyle = Record<string, string>;

export interface IfieldsKeyPressData {
  cardNumberFormattedLength?: number;
  cvvLength?: number;
}

type IfieldsWindow = Window & {
  setAccount?: (key: string, softwareName: string, softwareVersion: string) => void;
  enable3DS?: (environment: 'staging' | 'production', handler: (...values: string[]) => void) => void;
  setIfieldStyle?: (field: 'card-number' | 'cvv', style: Record<string, string>) => void;
  addIfieldKeyPressCallback?: (callback: (data: IfieldsKeyPressData) => void) => void;
  getTokens?: (success: (...values: unknown[]) => void, error: (error?: unknown) => void, timeout: number) => void;
  verify3DS?: (transaction: Record<string, unknown>) => void;
};

@Injectable({ providedIn: 'root' })
export class IfieldsService {
  private scriptPromise?: Promise<void>;

  loadScript(scriptUrl: string): Promise<void> {
    if (this.scriptPromise) return this.scriptPromise;

    this.scriptPromise = new Promise<void>((resolve, reject) => {
      const existing = document.querySelector(`script[src="${scriptUrl}"]`) as HTMLScriptElement | null;
      if (existing) {
        existing.dataset['loaded'] === 'true' ? resolve() : this.attachLoadHandlers(existing, resolve, reject);
        return;
      }
      const script = document.createElement('script');
      script.src = scriptUrl;
      script.async = true;
      this.attachLoadHandlers(script, resolve, reject);
      document.head.appendChild(script);
    });
    return this.scriptPromise;
  }

  initialize(
    publicKey: string,
    onVerify: (verification: IfieldsVerification) => void,
    fieldStyle: IfieldsFieldStyle = this.defaultFieldStyle()
  ): void {
    const api = window as IfieldsWindow;
    if (!publicKey || publicKey === 'REPLACE_WITH_IFIELDS_PUBLIC_KEY') throw new Error('iFields public key is not configured');
    if (!api.setAccount || !api.enable3DS) throw new Error('iFields script is not loaded');
    api.setAccount(publicKey, 'Tevuna', '1.0');
    api.enable3DS('production', (...values) => onVerify({
      x3dsActionCode: String(values[0] ?? ''),
      xCavv: String(values[1] ?? ''),
      xEci: String(values[2] ?? ''),
      xRefNum: String(values[3] ?? ''),
      x3dsAuthenticationStatus: String(values[4] ?? ''),
      x3dsSignatureVerificationStatus: String(values[5] ?? '')
    }));
    api.setIfieldStyle?.('card-number', fieldStyle);
    api.setIfieldStyle?.('cvv', fieldStyle);
  }

  private defaultFieldStyle(): IfieldsFieldStyle {
    return {
      border: '0',
      background: 'transparent',
      color: '#e9f8fd',
      'font-size': '16px',
      height: '56px',
      'line-height': '56px',
      padding: '0 16px',
      width: '100%',
      'box-sizing': 'border-box'
    };
  }

  getTokens(timeout = 30000): Promise<IfieldsTokens> {
    const api = window as IfieldsWindow;
    if (!api.getTokens) return Promise.reject(new Error('iFields script is not loaded'));
    return new Promise((resolve, reject) => {
      api.getTokens!((...values) => {
        const cardSut = this.tokenValue('card-number-token', values[0]);
        const cvvSut = this.tokenValue('cvv-token', values[1]);
        if (!cardSut || !cvvSut) {
          reject(new Error('iFields did not return card and CVV SUTs'));
          return;
        }
        resolve({ cardSut, cvvSut });
      }, error => reject(error instanceof Error ? error : new Error('Unable to tokenize card details')), timeout);
    });
  }

  addKeyPressCallback(callback: (data: IfieldsKeyPressData) => void): void {
    const api = window as IfieldsWindow;
    api.addIfieldKeyPressCallback?.(callback);
  }

  verify3DS(transaction: Record<string, unknown>): void {
    const api = window as IfieldsWindow;
    if (!api.verify3DS) throw new Error('iFields 3DS is not initialized');
    api.verify3DS(transaction);
  }

  private tokenValue(id: string, callbackValue: unknown): string {
    if (typeof callbackValue === 'string' && callbackValue) return callbackValue;
    const field = document.querySelector(`[data-ifields-id="${id}"]`) as HTMLInputElement | null;
    return field?.value || '';
  }

  private attachLoadHandlers(script: HTMLScriptElement, resolve: () => void, reject: (error: unknown) => void): void {
    script.addEventListener('load', () => { script.dataset['loaded'] = 'true'; resolve(); }, { once: true });
    script.addEventListener('error', () => reject(new Error('Unable to load iFields script')), { once: true });
  }
}
